import {
  buildPrompt,
  type EstateImage,
  type ImageOperation,
  type ImageScene,
  OPERATIONS,
  operationSpec,
} from '@acasa/shared'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/page-header'
import { PageShell } from '@/components/page-shell'
import { useSession } from '@/features/auth/use-auth'
import { sizedImageUrl } from '@/features/checklists/estate-images'
import { useChecklists, useEstateDetail } from '@/features/checklists/use-checklists'
import { GenerationHistory } from '@/features/image-ai/history'
import { JobCard } from '@/features/image-ai/job-card'
import { SCENE_LABELS } from '@/features/image-ai/operations'
import { useImageJobs } from '@/features/image-ai/use-image-jobs'

function Step({
  index,
  title,
  children,
  hint,
}: {
  index: number
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="bg-surface border-line border p-4">
      <h2 className="text-faint mb-3 text-[10px] font-medium tracking-[0.14em] uppercase">
        {index} · {title}
      </h2>
      {children}
      {hint && <p className="text-faint mt-2 text-[11px]">{hint}</p>}
    </section>
  )
}

export function ImageAiPage() {
  const [params, setParams] = useSearchParams()
  const estateId = params.get('estate') ? Number(params.get('estate')) : null

  const [query, setQuery] = useState('')
  const [selectedImageId, setSelectedImageId] = useState<number | null>(
    params.get('image') ? Number(params.get('image')) : null,
  )
  const [scene, setScene] = useState<ImageScene>('interior')
  const [operation, setOperation] = useState<ImageOperation>('interior_light')
  const [style, setStyle] = useState<string | null>(null)
  const [prompt, setPrompt] = useState('')
  const [tab, setTab] = useState<'queue' | 'history'>('queue')
  const { user } = useSession()

  const { data: overview } = useChecklists()
  const { data: detail, isPending: detailPending } = useEstateDetail(estateId)
  const { jobs: allJobs, configured, submit, remove } = useImageJobs()

  const spec = operationSpec(operation)

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!overview || needle.length < 2) return []
    return overview.estates
      .filter((estate) =>
        `${estate.externalNumber ?? ''} ${estate.title ?? ''}`.toLowerCase().includes(needle),
      )
      .slice(0, 8)
  }, [overview, query])

  const jobs = useMemo(
    () => (estateId === null ? [] : allJobs.filter((job) => job.estateId === estateId)),
    [allJobs, estateId],
  )

  /** estateId → ImmoNr, so a history row names its object rather than an id. */
  const estateLabels = useMemo(
    () =>
      new Map(
        (overview?.estates ?? []).map((estate) => [
          estate.estateId,
          estate.externalNumber ?? `#${estate.estateId}`,
        ]),
      ),
    [overview],
  )

  const images: EstateImage[] = detail?.images ?? []
  const selected = images.find((image) => image.id === selectedImageId) ?? null

  const chooseEstate = (id: number) => {
    setParams({ estate: String(id) })
    setSelectedImageId(null)
    setQuery('')
  }

  const promptMissing = spec.prompt?.required === true && prompt.trim().length === 0
  const canSubmit = selected !== null && !promptMissing

  // Preview only. The server builds the prompt it actually sends from the same
  // shared catalogue — the browser must not be able to choose the instruction,
  // or BASE_CONSTRAINTS could simply be left out.
  const modelPrompt = buildPrompt(spec, { prompt, style })

  const start = () => {
    if (!selected || !estateId) return
    submit.mutate({
      estateId,
      sourceImageId: selected.id,
      operation,
      prompt: spec.prompt ? prompt.trim() || null : null,
      style,
    })
  }

  return (
    <PageShell width="wide">
      <PageHeader
        title="Bild-KI"
        description="Objektfotos aufbereiten — Belichtung, Himmel, Retusche und Aufräumen, getrennt für Innen- und Außenaufnahmen."
      />

      {/* What it costs is an admin's business and lives with the other AI
          features' costs; an agent has no link to a page they cannot open. */}
      {user?.role === 'admin' && (
        <p className="text-faint -mt-3 mb-6 text-xs">
          Kosten unter{' '}
          <Link
            to="/settings#ki-nutzung"
            className="text-muted hover:text-ink underline underline-offset-2"
          >
            Einstellungen › KI-Nutzung
          </Link>
        </p>
      )}

      {/* Not configured is a state, not an error: the rest of the tool works,
          this one feature needs a key. Say which. */}
      {!configured && (
        <div className="border-warning/40 bg-warning/10 mb-6 border px-4 py-3">
          <p className="text-ink text-xs">
            <span className="font-medium">Bild-KI ist nicht konfiguriert.</span> Der Zugang zum
            Bilddienst fehlt in der <span className="font-mono text-[11px]">.env</span> des Servers.
            Aufträge lassen sich erst danach starten.
          </p>
        </div>
      )}

      {submit.isError && (
        <div className="border-danger/40 bg-danger/10 mb-6 border px-4 py-3">
          <p className="text-danger text-xs">
            {submit.error instanceof Error ? submit.error.message : 'Auftrag fehlgeschlagen'}
          </p>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,25rem)_minmax(0,1fr)] lg:items-start">
        {/* ------------------------------------------------------- input --- */}
        <div className="flex flex-col gap-4">
          <Step index={1} title="Objekt">
            {estateId && detail ? (
              <div className="flex items-start justify-between gap-3">
                <span className="min-w-0">
                  <span className="text-ink block truncate text-sm">
                    {detail.title ?? 'Ohne Titel'}
                  </span>
                  <span className="text-muted font-mono text-[11px]">
                    {detail.externalNumber ?? `#${detail.estateId}`}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setParams({})}
                  className="text-faint hover:text-ink shrink-0 text-[11px] underline underline-offset-2"
                >
                  Ändern
                </button>
              </div>
            ) : (
              <>
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="ImmoNr oder Objekttitel …"
                  className="border-line focus:border-brand bg-surface text-ink placeholder:text-faint w-full border px-3 py-2 text-sm outline-none"
                />
                {matches.length > 0 && (
                  <ul className="border-line mt-2 border">
                    {matches.map((estate) => (
                      <li key={estate.estateId}>
                        <button
                          type="button"
                          onClick={() => chooseEstate(estate.estateId)}
                          className="hover:bg-canvas flex w-full items-baseline gap-2 px-2.5 py-1.5 text-left text-xs"
                        >
                          <span className="text-muted shrink-0 font-mono text-[11px]">
                            {estate.externalNumber ?? estate.estateId}
                          </span>
                          <span className="text-ink min-w-0 truncate">
                            {estate.title ?? 'Ohne Titel'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {query.trim().length >= 2 && matches.length === 0 && (
                  <p className="text-faint mt-2 text-[11px]">Kein Treffer.</p>
                )}
              </>
            )}
          </Step>

          <Step index={2} title="Bild" hint={selected ? (selected.title ?? undefined) : undefined}>
            {!estateId && <p className="text-faint text-xs">Zuerst ein Objekt wählen.</p>}
            {estateId && detailPending && (
              <p className="text-faint text-xs">Bilder werden geladen …</p>
            )}
            {estateId && !detailPending && images.length === 0 && (
              <p className="text-faint text-xs">
                Für dieses Objekt liegen keine Bilder in onOffice.
              </p>
            )}

            {images.length > 0 && (
              <div className="grid max-h-64 grid-cols-4 gap-1.5 overflow-y-auto">
                {images.map((image) => {
                  const active = image.id === selectedImageId
                  return (
                    <button
                      key={image.id}
                      type="button"
                      onClick={() => setSelectedImageId(image.id)}
                      title={image.title ?? image.category}
                      aria-pressed={active}
                      className={`relative aspect-[4/3] overflow-hidden border transition-colors ${
                        active ? 'border-brand' : 'border-line hover:border-line-strong'
                      }`}
                    >
                      <img
                        src={sizedImageUrl(image, 160, 120)}
                        alt={image.title ?? image.category}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className="h-full w-full object-cover"
                      />
                    </button>
                  )
                })}
              </div>
            )}
          </Step>

          <Step index={3} title="Bearbeitung">
            {/* 16 operations in one list is a wall. Innen and Außen are also
                genuinely different jobs — the same "Personen entfernen" needs a
                different prompt in a living room and on a street. */}
            <div className="border-line mb-3 inline-flex w-fit border">
              {(['interior', 'exterior'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    setScene(value)
                    const first = OPERATIONS.find((item) => item.scene === value)
                    if (first) setOperation(first.id)
                    setStyle(null)
                    setPrompt('')
                  }}
                  aria-pressed={scene === value}
                  className={`border-line border-l px-3 py-1.5 text-[11px] transition-colors first:border-l-0 ${
                    scene === value
                      ? 'bg-brand-soft text-brand font-medium'
                      : 'text-faint hover:text-ink hover:bg-canvas'
                  }`}
                >
                  {SCENE_LABELS[value]}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-1">
              {OPERATIONS.filter((item) => item.scene === scene).map((item) => {
                const active = item.id === operation
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setOperation(item.id)
                      setStyle(null)
                      setPrompt('')
                    }}
                    aria-pressed={active}
                    className={`border px-3 py-2 text-left transition-colors ${
                      active ? 'border-brand bg-brand-soft' : 'border-line hover:border-line-strong'
                    }`}
                  >
                    <span className="flex items-baseline gap-1.5">
                      <span className={`text-xs ${active ? 'text-brand font-medium' : 'text-ink'}`}>
                        {item.label}
                      </span>
                      {item.experimental && (
                        <span className="text-warning text-[9px] tracking-wider uppercase">
                          Beta
                        </span>
                      )}
                    </span>
                    <span className="text-muted mt-0.5 block text-[11px]">{item.description}</span>
                  </button>
                )
              })}
            </div>

            {/*
              German practice is to label an image that shows the property in a
              state it is not in. Which of these count is the agency's call —
              but it should be a decision, not an oversight.
            */}
            {spec.substantive && (
              <p className="border-warning/40 bg-warning/10 text-ink mt-3 border px-2.5 py-2 text-[11px]">
                Verändert den dargestellten Zustand des Objekts. Ergebnis im Exposé als visualisiert
                kennzeichnen.
              </p>
            )}

            {spec.experimental && (
              <p className="text-faint mt-2 text-[11px]">
                Erfahrungsgemäß die unzuverlässigste Bearbeitung — Ergebnis genau prüfen, besonders
                die Raumproportionen.
              </p>
            )}

            {spec.styles && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {spec.styles.map((variant) => (
                  <button
                    key={variant.label}
                    type="button"
                    onClick={() => setStyle(style === variant.label ? null : variant.label)}
                    className={`border px-2 py-1 text-[11px] transition-colors ${
                      style === variant.label
                        ? 'border-brand text-brand bg-brand-soft'
                        : 'border-line text-muted hover:text-ink'
                    }`}
                  >
                    {variant.label}
                  </button>
                ))}
              </div>
            )}

            {spec.prompt && (
              <label className="mt-3 block">
                <span className="text-faint mb-1 block text-[10px] tracking-wider uppercase">
                  {spec.prompt.label}
                </span>
                <textarea
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  placeholder={spec.prompt.placeholder}
                  rows={3}
                  className="border-line focus:border-brand bg-surface text-ink placeholder:text-faint w-full resize-y border px-3 py-2 text-xs outline-none"
                />
              </label>
            )}

            {/* The prompts are the actual work of this feature; they need to be
                readable and tunable against a result, not buried in a file. */}
            <details className="mt-3">
              <summary className="text-faint hover:text-muted cursor-pointer text-[11px]">
                Prompt anzeigen
              </summary>
              <pre className="border-line bg-canvas text-muted mt-1.5 max-h-56 overflow-auto border p-2.5 font-mono text-[10px] leading-relaxed whitespace-pre-wrap">
                {modelPrompt}
              </pre>
            </details>

            <button
              type="button"
              onClick={start}
              disabled={!canSubmit || !configured || submit.isPending}
              className="bg-brand text-surface hover:bg-brand-strong mt-3 w-full px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
            >
              {submit.isPending ? 'Wird gestartet …' : 'Bearbeitung starten'}
            </button>
            {!selected && estateId && (
              <p className="text-faint mt-2 text-[11px]">Bitte ein Bild auswählen.</p>
            )}
            {promptMissing && selected && (
              <p className="text-faint mt-2 text-[11px]">
                Diese Bearbeitung braucht eine Beschreibung.
              </p>
            )}
          </Step>
        </div>

        {/* ------------------------------------------------------ output --- */}
        <div className="flex flex-col gap-3">
          {/* Two views over the same list: what is happening on this object,
              and everything ever generated. What it cost is not a third tab
              any more — that is Einstellungen › KI-Nutzung, admins only. */}
          <div className="border-line inline-flex w-fit border">
            {(
              [
                ['queue', `Aufträge${jobs.length > 0 ? ` (${jobs.length})` : ''}`],
                ['history', `Verlauf${allJobs.length > 0 ? ` (${allJobs.length})` : ''}`],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                aria-pressed={tab === value}
                className={`border-line border-l px-3 py-1.5 text-[11px] transition-colors first:border-l-0 ${
                  tab === value
                    ? 'bg-brand-soft text-brand font-medium'
                    : 'text-faint hover:text-ink hover:bg-canvas'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'queue' && (
            <div className="flex flex-col gap-4">
              {!estateId && (
                <div className="border-line text-faint border border-dashed px-4 py-10 text-center text-xs">
                  Kein Objekt gewählt. Der Verlauf zeigt alle bisherigen Bearbeitungen.
                </div>
              )}

              {estateId && jobs.length === 0 && (
                <div className="border-line text-faint border border-dashed px-4 py-10 text-center text-xs">
                  Für dieses Objekt noch keine Aufträge. Links ein Bild und eine Bearbeitung wählen.
                </div>
              )}

              {jobs.map((job) => (
                <JobCard key={job.id} job={job} onRemove={(id) => remove.mutate(id)} />
              ))}
            </div>
          )}

          {tab === 'history' && (
            <GenerationHistory
              jobs={allJobs}
              estateLabels={estateLabels}
              onRemove={(id) => remove.mutate(id)}
            />
          )}
        </div>
      </div>
    </PageShell>
  )
}
